/* -------------------------------------------------------------------------- */
/*  src/pages/Jobs.jsx                                                        */
/*  Grid-State (Filter/Sort/Paging + Suche) sicher in localStorage sichern    */
/*  – inklusive robuster Behandlung alter ISO-Strings und Selbstheilung       */
/* -------------------------------------------------------------------------- */
import * as React from 'react';
import { AppContext }   from '../AppContext';
import config           from '../config/config';
import { useNavigate }  from 'react-router-dom';

import {
  Grid, GridColumn as Column, GridToolbar
} from '@progress/kendo-react-grid';
import { process }        from '@progress/kendo-data-query';

import { Button }         from '@progress/kendo-react-buttons';
import { Input, Switch }  from '@progress/kendo-react-inputs';
import { DropDownList }   from '@progress/kendo-react-dropdowns';
import { createGlobalStyle } from 'styled-components'; 

import {
  Dialog, DialogActionsBar
} from '@progress/kendo-react-dialogs';
import { Loader }         from '@progress/kendo-react-indicators';

import {
  pencilIcon, trashIcon, plusCircleIcon
} from '@progress/kendo-svg-icons';

const DATA_ITEM_KEY = 'JobID';
const LS_KEY        = 'jobsGridState';

/* -------------------------------------------------------------------------- */
/* 1) (De-)Serialisierung für Date-Objekte – inkl. ISO-String-Erkennung       */
/* -------------------------------------------------------------------------- */
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;

const GlobalStyle = createGlobalStyle`
  .delete-confirmation-modal {
    /* Header – Titel mittig lassen */
    .k-dialog-titlebar {
      justify-content: space-between;
      align-items: center;
    }

    /* ------------------------------------------------------------------ */
    /* NEU: alle Varianten des Close-Buttons selektieren                  */
    /* ------------------------------------------------------------------ */
    .k-dialog-titlebar-actions .k-button,       /* generisch (neue Version) */
    .k-window-titlebar-actions .k-button,       /* generisch (alte Version) */
    .k-window-titlebar-action,                  /* spezifisch               */
    .k-dialog-titlebar-action {                 /* spezifisch               */
      width: 30px !important;
      height: 30px !important;
      flex: 0 0 30px;           /* sorgt dafür, dass er den Platz auch belegt */
      margin-left: auto;        /* schiebt ihn an den äußersten rechten Rand  */
    }

    /* Footer-Buttons */
    .k-button:not(.k-dialog-titlebar-action):not(.k-window-titlebar-action) {
      border-radius: 8px;
      min-width: 90px;
    }
  }
`;


const stringifyWithDates = (obj) =>
  JSON.stringify(obj, (_, v) =>
    v instanceof Date ? { __type: 'Date', value: v.toISOString() } : v
  );

/* -- Delete-Modal -------------------------------------------------------- */
const DeleteConfirmationModal = ({ visible, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <>
      <GlobalStyle />
      <Dialog
        title="Auftrag löschen"
        onClose={onCancel}
        width={400}
        className="delete-confirmation-modal"
      >
        {/* ⬇︎ NEU: Wrapper mit Padding */}
        <div style={{ padding: 10 }}>
          <p style={{ margin: '20px 0', textAlign: 'center', lineHeight: 1.2 }}>
            Möchten Sie den Auftrag wirklich löschen?
            <br />
            <span style={{ fontSize: '10.3px' }}>
              Hinweis: Diese Änderung kann nicht rückgängig gemacht werden!
            </span>
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 10,
              marginTop: 20
            }}
          >
            <Button onClick={onCancel}>Abbrechen</Button>
            <Button themeColor="primary" onClick={onConfirm}>
              Löschen
            </Button>
          </div>
        </div>
        {/* ⬆︎ Wrapper-Div Ende */}
      </Dialog>
    </>
  );
};

const parseWithDates = (str) => {
  try {
    return JSON.parse(str, (_, v) => {
      if (v && v.__type === 'Date') return new Date(v.value);
      if (typeof v === 'string' && ISO_RE.test(v)) {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d;
      }
      return v;
    });
  } catch {
    return null; // beschädigt
  }
};

/* Prüft, ob in den Datumsfiltern wirklich Date-Objekte stecken */
const isValidGridState = (state) =>
  !state?.dataState?.filter?.filters?.some(
    f => f.field === 'AusfuehrungszeitStart' && !(f.value instanceof Date)
  );

/* -------------------------------------------------------------------------- */
/* 2) Custom-Filter-Komponenten & Zellen (unverändert)                         */
/* -------------------------------------------------------------------------- */

/* -- JobActive-Filter ------------------------------------------------------ */
const JobActiveFilterCell = (props) => {
  const [value, setValue] = React.useState(props.value ?? 'All');
  const opts = [
    { text: 'Alle', value: 'All' },
    { text: 'Ja',   value: true },
    { text: 'Nein', value: false }
  ];
  React.useEffect(() => setValue(props.value ?? 'All'), [props.value]);

  const onChange = e => {
    const v = e.target.value.value;
    setValue(v);
    props.onChange({
      field: props.field,
      operator: v === 'All' ? '' : 'eq',
      value: v === 'All' ? '' : v,
      syntheticEvent: e.syntheticEvent
    });
  };

  return (
    <DropDownList
      data={opts}
      value={opts.find(o => o.value === value)}
      onChange={onChange}
      textField="text"
      dataItemKey="value"
      style={{ width: '100%' }}
    />
  );
};

/* -- Generischer Textfilter ------------------------------------------------ */
const CustomTextFilterCell = (props) => {
  const [val, setVal] = React.useState(props.value || '');
  React.useEffect(() => setVal(props.value || ''), [props.value]);
  const apply = v => props.onChange({
    field: props.field,
    operator: v ? 'contains' : '',
    value: v
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 4 }}>
      <Input value={val} onChange={e => setVal(e.value)}
             onKeyPress={e => e.key === 'Enter' && apply(val)} />
      <Button onClick={() => apply(props.value ? '' : val)}>
        {props.value ? 'Filter löschen' : 'Filtern'}
      </Button>
    </div>
  );
};

/* -- Mitarbeiter- & Avatar-Zellen ----------------------------------------- */
const EmployeeCell = ({ dataItem }) => {
  const list = dataItem.MitarbeiterArray || [];
  return <td>{list.length ? list.map((e, i) => <div key={i}>{e.name}</div>) : 'Unbekannt'}</td>;
};
const AvatarCell = ({ dataItem }) => {
  const list = dataItem.MitarbeiterArray || [];
  const size = list.length > 1 ? 25 : 50;
  return (
    <td>
      {list.length
        ? list.map((e, i) =>
            e.avatar
              ? <img
                  key={i}
                  src={e.avatar}
                  alt=""
                  loading="lazy"
                  style={{ width: size, height: size, borderRadius: '50%', margin: 2 }}
                />
              : <span key={i} style={{ fontSize: list.length > 1 ? 12 : 'inherit' }}>Kein Bild</span>
          )
        : 'Kein Bild'}
    </td>
  );
};

/* -- Datum-Zelle ----------------------------------------------------------- */
const DateCell = ({ dataItem }) => {
  if (!dataItem.AusfuehrungszeitStart) return <td>–</td>;
  const d   = dataItem.AusfuehrungszeitStart;
  const txt = d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
  return <td>{txt}</td>;
};

/* -- Adresse-Zelle --------------------------------------------------------- */
const AddressCell = ({ dataItem }) => {
  const addr = dataItem.Adresse;
  const url  = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  return (
    <td>
      <a href={url} target="_blank" rel="noopener noreferrer" className="address-link">
        {addr}
      </a>
    </td>
  );
};

/* -------------------------------------------------------------------------- */
/* 3) Haupt-Komponente                                                        */
/* -------------------------------------------------------------------------- */
const Jobs = () => {
  const navigate             = useNavigate();
  const { showNotification } = React.useContext(AppContext);

  /* ---------- a) Grid-State initial laden + validieren ------------------- */
  const defaultDS = {
    take: 100,
    skip: 0,
    sort: [{ field: 'AusfuehrungszeitStart', dir: 'desc' }]
  };

  let initialState = null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed = raw ? parseWithDates(raw) : null;
    initialState = isValidGridState(parsed) ? parsed : null;
    if (!initialState && raw) localStorage.removeItem(LS_KEY); // kaputten Eintrag löschen
  } catch { /* ignore */ }

  const [dataState, setDS]     = React.useState(initialState?.dataState || defaultDS);
  const [searchText, setSText] = React.useState(initialState?.searchText || '');

  const searchTextRef = React.useRef(searchText);
  React.useEffect(() => { searchTextRef.current = searchText; }, [searchText]);

  /* ---------- b) Sonstiger State ---------------------------------------- */
  const [allJobs, setAllJobs]  = React.useState([]);
  const [jobs, setJobs]        = React.useState([]);
  const [loading, setLoading]  = React.useState(true);
  const [dlg, setDlg]          = React.useState({ open: false, job: null });

  /* ---------- c) Änderungen -> localStorage ----------------------------- */
  React.useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, stringifyWithDates({ dataState, searchText }));
    } catch { /* quota */ }
  }, [dataState, searchText]);

  /* ---------- d) Grid-Daten verarbeiten --------------------------------- */
  const dataResult = React.useMemo(
    () => process(jobs, dataState),
    [jobs, dataState]
  );

  /* ---------- e) Jobs vom Server holen ---------------------------------- */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchJobs = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.host}/jobs/expanded`);
      const raw = await res.json();

      const enriched = raw.map(j => {
        const MitarbeiterArray = (j.MitarbeiterArray || []).map(e => ({
          ...e,
          avatar: e.avatar
            ? `${config.host}/get_image/${e.avatar.split('/').map(encodeURIComponent).join('/')}`
            : ''
        }));
        return {
          ...j,
          AusfuehrungszeitStart: j.AusfuehrungszeitStart
            ? new Date(j.AusfuehrungszeitStart)
            : null,
          MitarbeiterArray,
          Mitarbeiter: MitarbeiterArray.map(e => e.name).join(', ')
        };
      });

      setAllJobs(enriched);

      /* globale Suche erneut anwenden */
      const currentSearch = searchTextRef.current;
      if (currentSearch) {
        const v = currentSearch.toLowerCase();
        setJobs(enriched.filter(j =>
          j.Mitarbeiter.toLowerCase().includes(v)       ||
          j.Firma.toLowerCase().includes(v)             ||
          j.Adresse.toLowerCase().includes(v)           ||
          j.Kampfmittelbefund.toLowerCase().includes(v) ||
          (j.JobIDcustom || '').toLowerCase().includes(v) ||
          (j.Kurzbeschreibung || '').toLowerCase().includes(v)
        ));
      } else {
        setJobs(enriched);
      }

    } catch (err) {
      console.error(err);
      showNotification?.('error', 'Fehler beim Laden der Jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { fetchJobs(); }, []);


  /* ---------- f) Aktionen ------------------------------------------------ */
  const toggleActive = async (job, val) => {
    try {
      await fetch(`${config.host}/jobs/${job.JobID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ JobActive: val })
      });
      setAllJobs(prev => prev.map(j => j.JobID === job.JobID ? { ...j, JobActive: val } : j));
      setJobs(prev   => prev.map(j => j.JobID === job.JobID ? { ...j, JobActive: val } : j));
      showNotification?.('success', `Job ${val ? 'aktiviert' : 'deaktiviert'}.`);
    } catch {
      showNotification?.('error', 'Aktualisierung fehlgeschlagen.');
    }
  };

  const duplicateJob = async (job) => {
    try {
      const r = await fetch(`${config.host}/jobs/${job.JobID}/duplicate`, { method: 'POST' });
      if (!r.ok) throw new Error(await r.text());
      const { newJobID } = await r.json();
      showNotification?.('success', `Job dupliziert (ID: ${newJobID})`);
      fetchJobs();
    } catch (e) {
      console.error(e);
      showNotification?.('error', 'Duplizieren fehlgeschlagen.');
    }
  };

  const confirmDelete = async () => {
    try {
      await fetch(`${config.host}/jobs/${dlg.job.JobID}`, { method: 'DELETE' });
      showNotification?.('success', 'Job gelöscht.');
      fetchJobs();
    } catch {
      showNotification?.('error', 'Fehler beim Löschen.');
    } finally {
      setDlg({ open: false, job: null });
    }
  };

  /* ---------- g) Grid-Events ------------------------------------------- */
  const onDSChange = (e) => {
    const s = { ...e.dataState };
    if (!s.filter) s.filter = { logic: 'and', filters: [] };

    const normalize = (arr) =>
      arr.flatMap((f) => {
        if (f.filters) return [{ ...f, filters: normalize(f.filters) }];

        if (
          f.field === 'AusfuehrungszeitStart' &&
          (f.operator === 'eq' || f.operator === 'gte') &&
          f.value
        ) {
          const start = new Date(f.value); start.setHours(0, 0, 0, 0);
          const next  = new Date(start);   next.setDate(next.getDate() + 1);
          return [
            { field: f.field, operator: 'gte', value: start },
            { field: f.field, operator: 'lt',  value: next  }
          ];
        }
        return [f];
      });

    s.filter = { ...s.filter, filters: normalize(s.filter.filters) };
    setDS(s);
  };

  const globalSearch = e => {
    const typed = e.target.value;
    setSText(typed);

    const v = typed.toLowerCase();
    const f = allJobs.filter(j =>
      j.Mitarbeiter.toLowerCase().includes(v)       ||
      j.Firma.toLowerCase().includes(v)             ||
      j.Adresse.toLowerCase().includes(v)           ||
      j.Kampfmittelbefund.toLowerCase().includes(v) ||
      (j.JobIDcustom || '').toLowerCase().includes(v) ||
      (j.Kurzbeschreibung || '').toLowerCase().includes(v)
    );
    setJobs(f);
  };

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */
  return (
    <div id="Jobs" className="profile-page main-content">
      <div className="card-container">
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 20
        }}>
          <h3 className="card-title">Auftragsverwaltung</h3>
          <Button themeColor="primary" onClick={() => navigate('/create-jobs')}>
            Auftrag hinzufügen
          </Button>
        </div>

        {loading ? (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', marginTop: 100
          }}>
            <Loader size="large" type="infinite-spinner" />
            <p>Jobs werden geladen…</p>
          </div>
        ) : (
          <Grid
            style={{ height: '100%' }}
            dataItemKey={DATA_ITEM_KEY}
            data={dataResult}
            pageable={{ pageSizes: [50, 100, 150] }}
            sortable
            filterable
            resizable
            {...dataState}
            onDataStateChange={onDSChange}
          >
            <GridToolbar>
              <Input
                value={searchText}
                onChange={globalSearch}
                style={{
                  border: '2px solid #ccc', width: 170, height: 30, marginRight: 10
                }}
                placeholder="Suchen…"
              />
            </GridToolbar>

            {/* Reihenfolge laut Vorgabe */}
            <Column field="Avatar"      title="Bild"               width="80px"
                    filterable={false} cell={AvatarCell} />
            <Column field="Mitarbeiter" title="Mitarbeiter"        width="150px"
                    filterCell={CustomTextFilterCell} cell={EmployeeCell} />
            <Column field="AusfuehrungszeitStart" title="Datum"    width="210px"
                    filter="date" sortable cell={DateCell} />
            <Column field="JobIDcustom" title="Auftragsnr."        width="90px"
                    filterCell={CustomTextFilterCell} />
            <Column field="Kurzbeschreibung" title="Kurzbeschreibung" width="200px"
                    filterCell={CustomTextFilterCell} />
            <Column field="Firma"       title="Firma"              width="200px"
                    filterCell={CustomTextFilterCell} />
            <Column field="Adresse"     title="Adresse Einsatzort" width="*"
                    filterCell={CustomTextFilterCell} cell={AddressCell} />
            {/* Ausblendung des Kampfmittelbefundes
            <Column field="Kampfmittelbefund" title="Kampfmittelbefund" width="*"
                    filterCell={CustomTextFilterCell} />
            */}
            <Column field="JobActive"   title="Aktiv"              width="90px"
                    filterCell={JobActiveFilterCell}
                    cell={p => <td>{p.dataItem.JobActive ? 'Ja' : 'Nein'}</td>} />

            {/* Aktionen */}
            <Column title="Aktionen" width="200px" filterable={false}
              cell={p => {
                const j = p.dataItem;
                return (
                    <td>
                    <Switch
                      checked={j.JobActive}
                      onChange={e => toggleActive(j, e.value)}
                      tooltip={false}
                    />
                    <Button svgIcon={pencilIcon} fillMode="flat"
                            onClick={() => navigate(`/edit-jobs/${j.JobID}`)} />
                    <Button svgIcon={plusCircleIcon} fillMode="flat"
                            onClick={() => duplicateJob(j)} />
                    <Button svgIcon={trashIcon} fillMode="flat"
                            onClick={() => setDlg({ open: true, job: j })} />
                  </td>
                );
              }} />
          </Grid>
        )}

        {/* Delete Dialog */}
        <DeleteConfirmationModal
          visible={dlg.open}
          onConfirm={confirmDelete}
          onCancel={() => setDlg({ open: false, job: null })}
        />
      </div>
    </div>
  );
};

export default Jobs;
